import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CampaignEntityService } from '../../../campaign-util/shared/campaign-entity';
import { BreadcrumbBuilder } from '../../../../shared/ui/breadcrumb/breadcrumb-builder';
import * as Chartist from 'chartist';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { HttpClient } from '@angular/common/http';
import { CampaignCrudUrlBuilder } from '../../../campaign-util/shared/crud-url-builder';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-campaign-stats',
  templateUrl: './campaign-stats.component.html',
  styleUrls: ['./campaign-stats.component.scss'],
})
export class CampaignStatsComponent implements OnInit {

  @ViewChild('totalSalesChart', { static: true })
  totalSalesChart: ElementRef;
  @ViewChild('totalPrintsChart', { static: true })
  totalPrintsChart: ElementRef;
  @ViewChild('totalUploadsChart', { static: true })
  totalUploadsChart: ElementRef;

  totalSales = this.createChartStats('totalSales', 'Total Sales');
  totalPrints = this.createChartStats('totalPrints', 'Total Prints');
  totalUploads = this.createChartStats('totalUploads', 'Total Uploads');
  createDate = [
    moment().subtract(1, 'week').toDate(),
    moment().toDate(),
  ];

  dpConfig: Partial<BsDatepickerConfig> = {
    containerClass: 'theme-default right',
    dateInputFormat: 'YYYY-MM-DD',
  };

  constructor(
    private toastrService: ToastrService,
    private httpClient: HttpClient,
    private campaignEntity: CampaignEntityService,
    private crudUrlBuilder: CampaignCrudUrlBuilder,
    private breadcrumbBuilder: BreadcrumbBuilder,
  ) {
  }

  ngOnInit() {
    this.breadcrumbBuilder.clear();
    this.breadcrumbBuilder.add({
      label: 'Admin / Campaign Statistics',
      url: '/admin/stats',
    });

    this.loadChartData();
  }

  private loadChartData() {
    const dateFrom = moment(this.createDate[0]);
    const dateTo = moment(this.createDate[1]);

    if (!dateFrom.isValid() || !dateTo.isValid()) {
      this.toastrService.error('Invalid date range');
      return;
    }

    forkJoin([
      this.httpClient.get('/admin/stats/total-prints', {
        params: {
          createDate: [
            moment(this.createDate[0]).format('YYYY-MM-DD 00:00:00'),
            moment(this.createDate[1]).format('YYYY-MM-DD 23:59:59'),
          ],
        },
      }),
      this.httpClient.get('/admin/stats/total-uploads', {
        params: {
          createDate: [
            moment(this.createDate[0]).format('YYYY-MM-DD 00:00:00'),
            moment(this.createDate[1]).format('YYYY-MM-DD 23:59:59'),
          ],
        },
      }),
      this.httpClient.get('/admin/stats/total-sales', {
        params: {
          createDate: [
            moment(this.createDate[0]).format('YYYY-MM-DD 00:00:00'),
            moment(this.createDate[1]).format('YYYY-MM-DD 23:59:59'),
          ],
        },
      }),
    ]).pipe(finalize(() => {
      this.renderChart(
        this.totalSales,
        'total-sales',
        'totalSales',
        this.totalSalesChart,
      );
      this.renderChart(
        this.totalPrints,
        'total-prints',
        'totalPrints',
        this.totalPrintsChart,
      );
      this.renderChart(
        this.totalUploads,
        'total-uploads',
        'totalUploads',
        this.totalUploadsChart,
      );
    })).subscribe((response: any) => {
      this.totalSales.stats = response[0];
      this.totalPrints.stats = response[1];
      this.totalUploads.stats = response[2];
    });
  }

  private renderChart(item: any, itemName: string, seriesName: string, chartElementRef: ElementRef): void {

    item.data = {
      labels: [],
      series: [],
    };

    item.data.series.push({ name: itemName, data: [] });
    for (const [date, s] of Object.entries(item.stats)) {
      item.data.labels.push(date);
      let seriesIndex = 0;
      if (item.series[seriesName]) {
        item.data.series[seriesIndex++].data.push((<any>s));
      }
    }

    item.loading = false;

    new Chartist.Bar(chartElementRef.nativeElement, {
        labels: item.data.labels,
        series: item.data.series,
      },
      {
        axisX: {
          showGrid: false,
        },
        axisY: {
          onlyInteger: true,
          labelInterpolationFnc: function(value) {
            return Math.abs(value);
          },
        },
        height: 320,
        seriesBarDistance: 20,
      })
      .on('draw', (data) => {
        if (data.type === 'bar') {
          const randomClass = `ct-series-${data.series.name.toLowerCase()}-${Math.floor(Math.random() * 1000000)}`;
          const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
          data.element.addClass(randomClass);
          const el = document.querySelector(`.${randomClass}`) as any;
          el.style.stroke = randomColor;
          data.group.elem('text', {
            x: data.x2 - 0,
            y: data.y2 - 4,
            style: 'text-anchor: middle',
          }, 'ct-bar-label')
              .text(
                data.value.y,
              );
        }
      });
  }

  private createChartStats(seriesName: string, dataSeriesName: string): any {
    return {
      chart: null,
      loading: false,
      series: { [seriesName]: true, groups: true },
      stats: [],
      data: {
        labels: [],
        series: [{ name: dataSeriesName, data: [] }],
      },
      toggle: function(series: string) {
        this.series[series] = !this.series[series];
        this.render();
      },
    };
  }

  onDateChange($event: any) {
    if (!$event) {
      return;
    }
    this.createDate = $event;
    this.loadChartData();
  }
}
